// =============================================================================
// io/sync/bulkEdit.ts — the bulk write, and the delete.
// -----------------------------------------------------------------------------
// ── 🚨 COLUMN UPDATES, NEVER ROW UPSERTS. THIS IS WHY BULK EDIT IS LEGAL ──
//
// D46 says never save a row you did not fully read, and bulk edit touches trades
// the user never opened — so by that rule it should be impossible here.
//
// It is legal because this module does not write ROWS. It issues
// `update({named columns}).in('id', …)`, which names what it sets and cannot
// touch the other 45. The old client can afford a row write because it holds
// whole trades in memory; this client holds a 23-of-48-column projection, and an
// upsert of assembled row objects would destroy the other 25 columns on every
// selected trade — silently, and on the press that touches the most rows.
//
// ⚠ There is no `tradeToDb` call in this file and there must never be one. If a
// future change needs one, it needs D46's hydration ledger first.
//
// ── WHY THERE IS NO STALE-WRITE GUARD, STATED RATHER THAN OMITTED ──
//
// The journal writers hold a save when the row moved under them. This one does
// not, and the old client does not either. Two reasons, and neither is
// "we forgot":
//
//   1. The user has just been shown exactly what will be overwritten and on how
//      many trades (`bulkOverwrites` → a confirmation). Bulk edit is a deliberate
//      overwrite; a guard that blocks it is refusing the thing being asked for.
//   2. A per-row baseline check across 63 trades is 63 reads to protect a write
//      the user explicitly confirmed.
//
// ⚠ The consequence is real and worth knowing: if the other client edited one of
// these trades since this page loaded, this write wins. That is the accepted
// trade, not an oversight.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { bulkPatchFor, type BulkEditPlan, type BulkEditTrade } from '../../domain/bulkEdit';
import { reportFailureAs } from '../diagnostics/errorLog';
import { PAGE_ROWS } from './load';

/**
 * The columns bulk edit needs to READ.
 *
 * ⚠ Exactly the fields `bulkPatchFor` inspects — the preserve modes need the
 * current value, the boolean merge needs the current blob, and the overwrite
 * warning needs both. Nothing else, because a wider read here would tempt a
 * future caller to build a row from it.
 */
export const BULK_READ_SELECT =
  'id,pattern_id,exit_model_id,grade,mistake_tags,notes,risk_points,rr_planned,account_id,booleans';

export function bulkRowFromDb(r: Record<string, unknown>): BulkEditTrade {
  return {
    id: String(r['id'] ?? ''),
    patternId: (r['pattern_id'] ?? null) as string | null,
    exitModelId: (r['exit_model_id'] ?? null) as string | null,
    grade: (r['grade'] ?? null) as string | null,
    mistakeTags: Array.isArray(r['mistake_tags']) ? (r['mistake_tags'] as string[]) : [],
    notes: (r['notes'] ?? null) as string | null,
    riskPoints: (r['risk_points'] ?? null) as number | null,
    rrPlanned: (r['rr_planned'] ?? null) as number | null,
    accountId: (r['account_id'] ?? null) as string | null,
    booleans: (r['booleans'] ?? {}) as Record<string, unknown>,
  };
}

/**
 * Read the selected trades' editable fields.
 *
 * ⚠ Throws on the first bad page. A short read here does not look short — it
 * produces a preserve-mode decision and an overwrite warning computed from a
 * subset, so the user would confirm against numbers that understate what they
 * are about to change.
 */
export async function loadBulkEditRows(
  sb: SupabaseClient,
  ids: readonly string[],
): Promise<BulkEditTrade[]> {
  if (!ids.length) return [];
  const out: BulkEditTrade[] = [];
  // ⚠ CHUNKED. `in('id', …)` with a few hundred ids builds a URL long enough to
  // be rejected, and the failure looks like a read error rather than a length
  // problem.
  for (let i = 0; i < ids.length; i += PAGE_ROWS) {
    const chunk = ids.slice(i, i + PAGE_ROWS);
    const { data, error } = await sb.from('trades').select(BULK_READ_SELECT).in('id', chunk);
    if (error) {
      throw new Error(
        `bulk edit read failed (${chunk.length} of ${ids.length} trades): ${error.message}. ` +
          `Refusing to continue — the preserve modes and the overwrite warning are both ` +
          `computed from this read, so a partial one would understate what the save changes.`,
      );
    }
    for (const r of (data ?? []) as unknown as Record<string, unknown>[]) {
      out.push(bulkRowFromDb(r));
    }
  }
  return out;
}

export interface BulkApplyResult {
  /** Trades the plan actually changed. */
  updated: number;
  /** Trades the plan left alone — e.g. every field in preserve mode was filled. */
  skipped: number;
  error: string | null;
}

/**
 * Apply the plan.
 *
 * ⚠ GROUPED BY IDENTICAL PATCH. Most plans produce the same patch for every
 * trade, so the common case is ONE request rather than 63. Preserve modes and the
 * boolean merge are what split the groups — and those genuinely differ per trade,
 * so the grouping is a real optimisation rather than a guess.
 */
export async function applyBulkEdit(args: {
  sb: SupabaseClient;
  userId: string;
  rows: readonly BulkEditTrade[];
  plan: BulkEditPlan;
}): Promise<BulkApplyResult> {
  const { sb, userId, rows, plan } = args;

  const groups = new Map<string, { patch: Record<string, unknown>; ids: string[] }>();
  let skipped = 0;
  for (const t of rows) {
    const patch = bulkPatchFor(t, plan);
    // ⚠ `null` means this plan changes nothing on this trade. It must NOT become
    // an empty update: that would bump `updated_at` on a row nobody edited and
    // turn the other client's next save into a false conflict.
    if (!patch) { skipped++; continue; }
    const key = JSON.stringify(patch);
    const g = groups.get(key);
    if (g) g.ids.push(t.id);
    else groups.set(key, { patch, ids: [t.id] });
  }

  let updated = 0;
  for (const { patch, ids } of groups.values()) {
    for (let i = 0; i < ids.length; i += PAGE_ROWS) {
      const chunk = ids.slice(i, i + PAGE_ROWS);
      const { data, error } = await sb
        .from('trades')
        .update(patch)
        // ⚠ `user_id` as well as the ids. RLS already scopes this, but a bulk
        // write is the one place worth belt-and-braces: an id from another user
        // in the selection must fail to match rather than rely on a policy.
        .eq('user_id', userId)
        .in('id', chunk)
        .select('id');
      if (error) {
        // 🚨 A HALF-APPLIED BULK EDIT. The count in the message is the only record
        // of how far it got, so the log has to keep the sentence verbatim.
        return {
          updated, skipped,
          error: reportFailureAs(
            'bulk edit',
            error,
            `Bulk edit failed after ${updated} trade(s): ${error.message}. ` +
              `The trades already updated were saved; the rest were not.`,
          ),
        };
      }
      updated += Array.isArray(data) ? data.length : 0;
    }
  }

  return { updated, skipped, error: null };
}

export interface BulkDeleteResult {
  deleted: number;
  error: string | null;
}

/**
 * Delete the selected trades.
 *
 * ⚠ A HARD DELETE, matching the old client's bulk Delete — there is no soft
 * delete on `trades` (the 30-day grace pattern exists on ACCOUNTS, not here). The
 * caller must confirm first and must say how many rows are going, because nothing
 * downstream can undo this.
 */
export async function deleteTrades(
  sb: SupabaseClient,
  userId: string,
  ids: readonly string[],
): Promise<BulkDeleteResult> {
  if (!ids.length) return { deleted: 0, error: null };
  let deleted = 0;
  for (let i = 0; i < ids.length; i += PAGE_ROWS) {
    const chunk = ids.slice(i, i + PAGE_ROWS);
    const { data, error } = await sb
      .from('trades').delete().eq('user_id', userId).in('id', chunk).select('id');
    if (error) {
      // 🚨 Same as above, and worse: this one is a hard delete with nothing
      // downstream to undo it, so the partial count is the whole audit trail.
      return {
        deleted,
        error: reportFailureAs(
          'delete trades',
          error,
          `Delete failed after ${deleted} trade(s): ${error.message}. ` +
            `The trades already deleted are gone; the rest remain.`,
        ),
      };
    }
    deleted += Array.isArray(data) ? data.length : 0;
  }
  return { deleted, error: null };
}
